import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";

const STATUS_META = {
  available: { label: "Available", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20", dot: "bg-emerald-400" },
  occupied: { label: "Occupied", cls: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20", dot: "bg-[#D4AF37]" },
  reserved: { label: "Reserved", cls: "bg-blue-400/10 text-blue-300 border-blue-400/20", dot: "bg-blue-400" },
  maintenance: { label: "Maintenance", cls: "bg-orange-500/10 text-orange-300 border-orange-500/20", dot: "bg-orange-400" },
};

export default function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/rooms"); setRooms(data); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const counts = { available: 0, occupied: 0, reserved: 0, maintenance: 0 };
    rooms.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [rooms]);

  async function toggleStatus(id, currentStatus) {
    const next = currentStatus === "maintenance" ? "available" : "maintenance";
    try { await api.patch(`/rooms/${id}`, { status: next }); toast.success(`Room → ${next}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  }

  return (
    <div className="space-y-8" data-testid="rooms-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Rooms</div>
        <h1 className="font-display text-3xl md:text-4xl">Room inventory & status</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <div key={k} className="bg-[#12182B] border border-white/5 rounded-xl p-5" data-testid={`room-summary-${k}`}>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${m.dot}`} />
              <span className="text-xs uppercase tracking-widest text-white/50">{m.label}</span>
            </div>
            <div className="font-display text-3xl mt-3">{grouped[k] || 0}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl overflow-hidden" data-testid="rooms-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/5">
                <th className="px-5 py-4">Room No.</th>
                <th className="px-5 py-4">Type</th>
                <th className="px-5 py-4">Floor</th>
                <th className="px-5 py-4">Rate / night</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center text-white/50 py-12">Loading…</td></tr>}
              {!loading && rooms.map((r) => {
                const m = STATUS_META[r.status] || STATUS_META.available;
                return (
                  <tr key={r.id} className="border-b border-white/5 last:border-none hover:bg-[#1A2235] transition" data-testid={`room-row-${r.room_number}`}>
                    <td className="px-5 py-4 font-medium">{r.room_number}</td>
                    <td className="px-5 py-4 text-white/80">{r.room_type}</td>
                    <td className="px-5 py-4 text-white/60">{r.floor}</td>
                    <td className="px-5 py-4 text-white/80">₹{Math.round(r.base_rate).toLocaleString("en-IN")}</td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${m.cls}`}>{m.label}</span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => toggleStatus(r.id, r.status)} className="text-xs text-[#D4AF37] hover:text-[#F2D06B]" data-testid={`toggle-status-${r.room_number}`}>
                        {r.status === "maintenance" ? "Mark available" : "Mark maintenance"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
